import React, { useState, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import configService, { Language } from '../../services/configService';

interface LanguageSelectorProps {
  selectedLanguage: string;
  onChange: (languageId: string) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ selectedLanguage, onChange }) => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>('');

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languagesData = await configService.getLanguages();
        setLanguages(languagesData);
        
        // Find the language that matches the selected name
        const language = languagesData.find(lang => lang.name === selectedLanguage);
        
        if (language) {
          setSelectedLanguageId(language._id);
          onChange(language._id);
        } 
        // Set default language if current selection is not in the list
        else if (languagesData.length > 0) {
          onChange(languagesData[0]._id);
          setSelectedLanguageId(languagesData[0]._id);
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLanguages();
  }, [selectedLanguage, onChange]);

  // Handle language selection change
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const languageName = e.target.value;
    const language = languages.find(lang => lang.name === languageName);
    
    if (language) {
      setSelectedLanguageId(language._id);
      onChange(language._id);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Globe size={18} className="text-primary-light dark:text-primary-dark" />
        <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
          Interview Language
        </label>
      </div>
      <div className="relative bg-background-light/70 dark:bg-background-dark/70 rounded-lg border border-border-light dark:border-border-dark px-1 py-1 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
        <select 
          value={selectedLanguage}
          onChange={handleLanguageChange}
          className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
          disabled={isLoading}
        >
          {isLoading ? (
            <option value="">Loading languages...</option>
          ) : languages.length > 0 ? (
            languages.map(language => (
              <option key={language._id} value={language.name}>
                {language.name}
              </option>
            ))
          ) : (
            <option value="English">English</option>
          )}
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDown size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark" />
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector; 